import { useState } from 'react'

const steps = [
  {
    id: 'create',
    title: 'Define the bar',
    description: 'Set principles, competencies, level expectations, and headcount targets. The bar is established before sourcing begins.',
    image: '/images/workflow-create.jpg',
  },
  {
    id: 'source',
    title: 'Collect evidence',
    description: 'Gather resumes, GitHub profiles, async work samples, and forwarded recruiter conversations. You choose what to share.',
    image: '/images/workflow-source.jpg',
  },
  {
    id: 'screen',
    title: 'Evaluate consistently',
    description: 'Human and AI evaluators submit structured scorecards. Every assessment uses the same rubric, weighted by role and level.',
    image: '/images/workflow-screen.jpg',
  },
  {
    id: 'hire',
    title: 'Decide with confidence',
    description: 'See aggregated signal, confidence scores, and reasoning. A clear decision record is created for every candidate.',
    image: '/images/workflow-hire.jpg',
  },
]

export function Workflow() {
  const [activeStep, setActiveStep] = useState('create')

  return (
    <section id="workflow" className="py-24 bg-white">
      <div className="mx-auto max-w-7xl section-padding">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
            From evidence to decision in four steps
          </h2>
          <p className="text-lg text-surface-600">
            A signal-acquisition protocol that ensures sufficient, comparable evidence before any decision is made.
          </p>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Steps */}
          <div className="space-y-4">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                  activeStep === step.id
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-surface-200 hover:border-surface-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    activeStep === step.id
                      ? 'border-brand-500 bg-brand-500'
                      : 'border-surface-300'
                  }`}>
                    {activeStep === step.id && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className={`font-semibold mb-1 ${
                      activeStep === step.id ? 'text-brand-700' : 'text-surface-900'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="text-surface-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Image area */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl bg-surface-100 overflow-hidden shadow-lg border border-surface-200">
              {steps.find(s => s.id === activeStep)?.image ? (
                <img 
                  src={steps.find(s => s.id === activeStep)?.image || ''} 
                  alt={steps.find(s => s.id === activeStep)?.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-brand-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        {activeStep === 'source' && (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                        )}
                        {activeStep === 'screen' && (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                        )}
                        {activeStep === 'hire' && (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                      </svg>
                    </div>
                    <p className="text-surface-500 text-sm">
                      {steps.find(s => s.id === activeStep)?.title}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Dots navigation */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    activeStep === step.id ? 'bg-brand-500' : 'bg-surface-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

